import React, { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ScanDNIComponent from "../../../components/common/PL/ScanDNI/ScanDNI";
import { RoutePaths } from "../../../utils/Items";
import { IBlackUser, ISearchUser } from "../../../store/types/userTypes";
import { useStore } from "../../../hooks/useStore";
import DialogConfirmation from "../../../components/common/PL/Dialog/DialogConfirmation";
import BlacklistDialog from "../../../components/common/PL/Dialog/BlacklistDialog";
import {
  isRegistrationMode,
  isRegistroIngresoNavigationIntent,
  RegistroIngresoNavigationState,
} from "../RegistroIngreso/registroIngresoNavigation";
import { ScanDniNavigationState } from "./scanDniNavigation";
import { useRegistroIngresoSession } from "../RegistroIngreso/session/RegistroIngresoSessionContext";
import {
  processRegistroIngresoScan,
  finalizeBuildingMismatchScan,
  PendingPassScanData,
} from "../RegistroIngreso/utils/processScan";
import { isFullAccessRestriction } from "../RegistroIngreso/utils/blacklistRestriction";
import usePLScannerListener from "../hooks/usePLScannerListener";

const ScanDNIView = () => {
  const history = useHistory();
  const location = useLocation<ScanDniNavigationState | undefined>();
  const safeLocationState = location.state ?? {};
  const session = useRegistroIngresoSession();
  const { authStore } = useStore();
  const isScanningRef = useRef(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [pendingPassScanData, setPendingPassScanData] = useState<PendingPassScanData | null>(null);
  const [scanKey, setScanKey] = useState(0);
  const [blockedData, setBlockedData] = useState<IBlackUser | null>(null);

  const mode = isRegistrationMode(safeLocationState.mode) ? safeLocationState.mode : "entry";
  const intent = isRegistroIngresoNavigationIntent(safeLocationState.intent)
    ? safeLocationState.intent
    : "append-tab";

  const handleScan = async (results: ISearchUser) => {
    if (isScanningRef.current) return;
    isScanningRef.current = true;

    await processRegistroIngresoScan({
      results,
      mode,
      intent,
      session,
      authStore,
      history,
      onBuildingMismatch: (pending) => {
        setPendingPassScanData(pending);
        setBuildingDialogOpen(true);
      },
      onError: (message) => {
        setErrorMessage(message);
        setErrorDialogOpen(true);
        isScanningRef.current = false;
      },
      onFullyBlocked: (data) => {
        setBlockedData(data);
      },
    });
  };

  usePLScannerListener({ enabled: true, onScan: handleScan });

  const handleConfirmBuildingMismatch = async () => {
    setBuildingDialogOpen(false);
    if (pendingPassScanData) {
      await finalizeBuildingMismatchScan({
        pending: pendingPassScanData,
        mode,
        intent,
        session,
        history,
        onFullyBlocked: (data) => {
          setBlockedData(data);
        },
      });
      setPendingPassScanData(null);
    }
  };

  const handleCancelBuildingMismatch = () => {
    setBuildingDialogOpen(false);
    setPendingPassScanData(null);
    setScanKey((prev) => prev + 1);
    isScanningRef.current = false;
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setScanKey((prev) => prev + 1);
    isScanningRef.current = false;
  };

  const handleBlockedDialogClose = () => {
    setBlockedData(null);
    setScanKey((prev) => prev + 1);
    isScanningRef.current = false;
  };

  const handleManualSubmit = (values: { country: string; dni: string }, userData?: any) => {
    if (isFullAccessRestriction(userData?.blacklistedData)) {
      const tab = session.findExistingTab(values.dni, values.country, mode);
      if (tab) session.closeTab(tab.id);
      setBlockedData(userData.blacklistedData);
      return;
    }

    const state: RegistroIngresoNavigationState = {
      mode,
      intent: "keep-session",
    };

    history.push({
      pathname: RoutePaths.LogisticParkEntryRegistration,
      state,
    });
  };

  return (
    <>
      <ScanDNIComponent
        key={scanKey}
        onBack={() => history.goBack()}
        onScan={handleScan}
        onManualSubmit={handleManualSubmit}
        mode={mode}
      />
      <DialogConfirmation
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        onConfirm={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
      <DialogConfirmation
        open={buildingDialogOpen}
        onClose={handleCancelBuildingMismatch}
        onConfirm={handleConfirmBuildingMismatch}
        title="Advertencia"
        message="Esta autorización es para otro establecimiento"
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
      />
      <BlacklistDialog
        open={!!blockedData}
        blacklistedData={blockedData}
        onReject={handleBlockedDialogClose}
        onAllow={handleBlockedDialogClose}
      />
    </>
  );
};

export default ScanDNIView;
